import { z } from "zod";

import { AGENT_IDENTITY_SOURCES } from "./agent.models";

/** The unit of agent traffic a signal classifies. */
export const SIGNAL_SCOPES = ["span", "trace", "session"] as const;
export type SignalScope = (typeof SIGNAL_SCOPES)[number];

/** Sentiment of a signal outcome label — drives trend direction and chart color. */
export const SIGNAL_SENTIMENTS = ["positive", "negative", "neutral"] as const;
export type SignalSentiment = (typeof SIGNAL_SENTIMENTS)[number];

/**
 * Classifier types a user can create. Excludes the reserved `regex` type, which
 * is part of the full `SignalClassifierType` (`@kuzco/db`) but not user-creatable.
 */
export const CREATABLE_SIGNAL_CLASSIFIER_TYPES = ["binary", "string"] as const;
export type CreatableSignalClassifierType =
  (typeof CREATABLE_SIGNAL_CLASSIFIER_TYPES)[number];

// The following mirror the DB `pgEnum` columns exactly (source of truth:
// `@kuzco/db` SignalsTable `signal_state`, SignalVersionsTable
// `signal_classifier_type`). They back the signal-list output schema, so they
// must include every value a stored row can hold — including the reserved
// `regex` classifier that is not user-creatable but can exist on older rows.
const signalStateSchema = z.enum(["draft", "active", "disabled"]);
const signalClassifierTypeSchema = z.enum(["binary", "string", "regex"]);
const signalSentimentSchema = z.enum(SIGNAL_SENTIMENTS);

/**
 * One row of the project-wide signal list (`signals.listByProject`): identity,
 * lifecycle state, and the classifier/label metadata carried by the signal's
 * current version. Classifier fields are null when the signal has no published
 * version yet (a draft).
 */
export const signalListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  agentId: z.string(),
  agentIdentityValue: z.string(),
  state: signalStateSchema,
  classifierType: signalClassifierTypeSchema.nullable(),
  sampleRatePercent: z.number().nullable(),
  labels: z.array(z.string()),
  labelSentiments: z.record(z.string(), signalSentimentSchema),
});

/**
 * One time-bucket of a signal's label-volume distribution
 * (`signals.distribution`). `count` is a ClickHouse `count()` (UInt64): its
 * JSONEachRow serialization is an unquoted NUMBER, not a string, despite the
 * store's `string` type annotation. `z.coerce.string()` accepts that number and
 * normalizes it to the string the callers already expect — a plain `z.string()`
 * rejected the real value and 500'd the endpoint.
 */
export const signalDistributionBucketSchema = z.object({
  bucket: z.string(),
  label: z.string(),
  count: z.coerce.string(),
});

/**
 * A signal's raw stored row (the whole `SignalsTable`). Enums mirror the DB
 * `pgEnum`s exactly: `agent_identity_source`, `signal_scope`, `signal_state`.
 */
export const signalRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  teamId: z.string(),
  projectId: z.string().nullable(),
  agentId: z.string(),
  agentIdentityValue: z.string(),
  agentIdentitySource: z.enum(AGENT_IDENTITY_SOURCES),
  scope: z.enum(SIGNAL_SCOPES),
  state: signalStateSchema,
  currentVersionId: z.string().nullable(),
  templateKey: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
});

/**
 * A signal row decorated with the current version's classifier/label metadata
 * and a recent-label-volume count (`signals.list`, the per-agent list). The
 * classifier fields are null / empty for a draft signal with no published
 * version.
 */
export const signalWithMetricsSchema = signalRowSchema.extend({
  classifierType: signalClassifierTypeSchema.nullable(),
  sampleRatePercent: z.number().nullable(),
  recentLabelCount: z.number(),
  labels: z.array(z.string()),
  labelSentiments: z.record(z.string(), signalSentimentSchema),
});

/**
 * A signal version's full stored row (`SignalVersionsTable`). Unlike the list
 * metrics, a version always has a `classifierType`, `sampleRatePercent`, and
 * label maps (they are non-null columns). `judge*` fields are null for
 * classifiers that don't use an LLM judge.
 */
export const signalVersionSchema = z.object({
  id: z.string(),
  signalId: z.string(),
  version: z.number(),
  classifierType: signalClassifierTypeSchema,
  prompt: z.string(),
  labels: z.array(z.string()),
  labelSentiments: z.record(z.string(), signalSentimentSchema),
  sampleRatePercent: z.number(),
  judgeRouteId: z.string().nullable(),
  judgeCanonicalModelAlias: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.date(),
});

/**
 * A signal with its current published version (`signals.getById`).
 * `currentVersion` is null for a draft signal that has no published version.
 */
export const signalWithVersionSchema = z.object({
  signal: signalRowSchema,
  currentVersion: signalVersionSchema.nullable(),
});
